// src/components/dashboard/DashboardOverview.js - ENHANCED with Unified Booking System
import {
  Calendar,
  Clock,
  MessageCircle,
  Phone,
  Plus
} from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useBookings } from '../../hooks/useBookings';
import { useBusiness } from '../../hooks/useBusiness';

const DashboardOverview = ({ onCreateBooking }) => {
  const { user } = useAuth();
  
  const { businessProfile } = useBusiness();
  
  // Use unified booking system with calendar integration
  const {
    bookings,
    isCalendarIntegrated,
    calendarStatus,
    loading: bookingsLoading
  } = useBookings(user?.businessId);

  const [selectedDay, setSelectedDay] = useState('today');

  // ==========================================
  // MAIN RENDER
  // ==========================================
  
  return (
    <div className="p-4 max-w-3xl mx-auto">
      {/* ── Status strip ── */}
      <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 mb-4 flex flex-wrap items-center gap-4">

        {/* Booking number */}
        <div className="flex items-center gap-2 text-sm">
          <Phone className="w-4 h-4 text-gray-400" />
          <span className="text-gray-500">Booking line:</span>
          <span className="font-medium text-gray-900">
            {businessProfile?.whatsapp_number
              ? businessProfile.whatsapp_number.replace('whatsapp:', '')
              : user?.businessPhone || '—'}
          </span>
        </div>

        <div className="h-4 w-px bg-gray-200 hidden sm:block" />

        {/* Voice status */}
        <div className="flex items-center gap-1.5 text-sm">
          <span className={`w-2 h-2 rounded-full ${isCalendarIntegrated ? 'bg-green-500' : 'bg-gray-300'}`} />
          <span className="text-gray-600">
            Voice {isCalendarIntegrated ? 'active' : 'setup needed'}
          </span>
        </div>

        {/* WhatsApp status */}
        <div className="flex items-center gap-1.5 text-sm">
          <MessageCircle className="w-4 h-4 text-green-500" />
          <span className="text-gray-600">WhatsApp active</span>
        </div>

        {/* Calendar status */}
        <div className="flex items-center gap-1.5 text-sm">
          <span className={`w-2 h-2 rounded-full ${calendarStatus === 'active' ? 'bg-green-500' : 'bg-amber-400'}`} />
          <span className="text-gray-600">
            Calendar {calendarStatus === 'active' ? 'synced' : calendarStatus || 'not synced'}
          </span>
        </div>

      </div>

      {/* ── Day selector ── */}
      <div className="flex items-center justify-between mb-4">

        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setSelectedDay('today')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              selectedDay === 'today'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Today
          </button>
          <button
            onClick={() => setSelectedDay('tomorrow')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              selectedDay === 'tomorrow'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Tomorrow
          </button>
        </div>

        <button
          onClick={onCreateBooking}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Booking
        </button>

      </div>

      {/* ── Appointment list ── */}
      {(() => {
        const today = new Date();
        const targetDate = selectedDay === 'today' ? today : new Date(today.getTime() + 86400000);
        const targetDateStr = targetDate.toISOString().split('T')[0]; // YYYY-MM-DD

        const dayLabel = selectedDay === 'today'
          ? today.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' })
          : targetDate.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' });

        const dayBookings = bookings
          .filter(b => {
            const bDate = b.date || b.eventStart || '';
            return bDate.startsWith(targetDateStr) && b.status !== 'cancelled';
          })
          .sort((a, b) => (a.time || a.eventStart || '').localeCompare(b.time || b.eventStart || ''));

        return (
          <div className="bg-white border border-gray-200 rounded-lg">

            {/* Date header */}
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">{dayLabel}</h2>
              <span className="text-sm text-gray-500">
                {dayBookings.length === 0
                  ? 'No appointments'
                  : `${dayBookings.length} appointment${dayBookings.length !== 1 ? 's' : ''}`}
              </span>
            </div>

            {/* Loading state */}
            {bookingsLoading && (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                Loading appointments…
              </div>
            )}

            {/* Empty state */}
            {!bookingsLoading && dayBookings.length === 0 && (
              <div className="px-4 py-10 text-center">
                <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No appointments {selectedDay}</p>
                <button
                  onClick={onCreateBooking}
                  className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  + Add one
                </button>
              </div>
            )}

            {/* Appointment rows */}
            {!bookingsLoading && dayBookings.map((booking, index) => {
              const time = booking.time
                ? (() => {
                    const [h, m] = booking.time.split(':').map(Number);
                    const period = h >= 12 ? 'PM' : 'AM';
                    const hour = h % 12 || 12;
                    return `${hour}:${m.toString().padStart(2, '0')} ${period}`;
                  })()
                : '—';

              const statusColour =
                booking.status === 'confirmed'  ? 'bg-green-100 text-green-700' :
                booking.status === 'rescheduled'? 'bg-amber-100 text-amber-700' :
                                                  'bg-gray-100 text-gray-600';

              return (
                <div
                  key={booking.id || index}
                  className="px-4 py-3 flex items-center gap-4 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                >
                  {/* Time */}
                  <div className="w-20 shrink-0 text-sm font-medium text-gray-900 tabular-nums">
                    {time}
                  </div>

                  {/* Service + customer */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate capitalize">
                      {(booking.serviceType || booking.service_type || 'Appointment').replace(/_/g, ' ')}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {booking.customerName || booking.customer_name || 'Customer'}
                    </p>
                  </div>

                  {/* Duration */}
                  <div className="hidden sm:flex items-center gap-1 text-xs text-gray-400 shrink-0">
                    <Clock className="w-3 h-3" />
                    {booking.durationMinutes || booking.duration_minutes || 30} min
                  </div>

                  {/* Status badge */}
                  <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${statusColour}`}>
                    {booking.status || 'confirmed'}
                  </span>

                </div>
              );
            })}

          </div>
        );
      })()}
    </div>
  );
};

export default DashboardOverview;
